import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, TrendingUp, CheckCircle, Target, BarChart3, FileCheck, 
  Search, GraduationCap, ArrowRight, Quote, Sparkles, Lock,
  ChevronRight, Zap
} from 'lucide-react';
import CyberBackground from '../components/CyberBackground';
import SecurityInsights from '../components/SecurityInsights';

const HomePage = () => {
  const services = [
    {
      icon: Shield,
      title: 'Consultoria em Cybersegurança',
      description: 'Políticas, arquitetura de segurança, gestão de acessos e resposta a incidentes.',
      link: '/servicos#consultoria',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: BarChart3,
      title: 'Governança, Riscos e Compliance',
      description: 'Estruturação de governança, gestão de riscos e definição de KPIs estratégicos.',
      link: '/servicos#grc',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: FileCheck,
      title: 'ISO 27001 & Conformidade',
      description: 'Gap analysis, roadmap de implementação e suporte até a certificação.',
      link: '/servicos#iso27001',
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: Search,
      title: 'Pentest & Forense Digital',
      description: 'Testes de penetração, simulação de ataques e investigação de incidentes.',
      link: '/servicos#pentest',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: GraduationCap,
      title: 'Conscientização e Treinamentos',
      description: 'Programas de awareness, simulações de phishing e relatórios de engajamento.',
      link: '/servicos#treinamentos',
      color: 'from-pink-500 to-rose-500'
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Visão Estratégica de Riscos',
      description: 'Visão consolidada e executiva de todos os riscos da organização.'
    },
    {
      icon: BarChart3,
      title: 'Dashboards de KPIs',
      description: 'Indicadores em tempo real para tomada de decisão assertiva.'
    },
    {
      icon: TrendingUp,
      title: 'Redução de Tempo em Auditorias',
      description: 'Evidências organizadas e prontamente disponíveis.'
    },
    {
      icon: CheckCircle,
      title: 'Alinhamento Regulatório',
      description: 'Conformidade com múltiplos frameworks e normas.'
    }
  ];

  const testimonials = [
    {
      initials: 'CISO',
      role: 'Chief Information Security Officer',
      sector: 'Setor Financeiro',
      quote: 'A consultoria transformou nossa abordagem de segurança. Com o SmartAudit, reduzimos o tempo de preparação para auditorias em 70%.'
    },
    {
      initials: 'DPO',
      role: 'Diretora de Compliance',
      sector: 'Setor de Tecnologia',
      quote: 'A expertise em GRC nos ajudou a estruturar uma governança sólida. O suporte na certificação ISO 27001 foi fundamental para nosso crescimento.'
    },
    {
      initials: 'CTO',
      role: 'Chief Technology Officer',
      sector: 'Setor de Saúde',
      quote: 'Parceria estratégica de longo prazo. A equipe não só implementou soluções técnicas, mas capacitou todo nosso time interno.'
    },
    {
      initials: 'GRC',
      role: 'Gerente de Riscos e Compliance',
      sector: 'Setor de Varejo',
      quote: 'O SmartAudit revolucionou nossa gestão de riscos. A visão consolidada de todos os frameworks em um único dashboard nos dá clareza para priorizar investimentos.'
    },
    {
      initials: 'CSO',
      role: 'Chief Security Officer',
      sector: 'Setor Industrial',
      quote: 'A integração automática entre ISO 27001 e PPSI 2.0 no SmartAudit eliminou retrabalho. O que antes levava semanas, agora fazemos em dias.'
    },
    {
      initials: 'DIR',
      role: 'Diretor de TI',
      sector: 'Setor de Serviços',
      quote: 'Os KPIs executivos do SmartAudit mudaram a forma como reportamos segurança para o board. Dados claros, objetivos e em tempo real.'
    },
    {
      initials: 'AUD',
      role: 'Coordenador de Auditoria Interna',
      sector: 'Setor Financeiro',
      quote: 'A rastreabilidade de evidências no SmartAudit é impressionante. Auditorias externas que duravam 2 semanas agora são concluídas em 3 dias.'
    }
  ];

  const frameworks = ['ISO 27001', 'NIST CSF 2.0', 'BIA', 'DPIA', 'TPRM', 'SOX', 'Riscos'];

  return (
    <>
      <Helmet>
        <title>DMM Security - Consultoria em Segurança da Informação e GRC</title>
        <meta name="description" content="Consultoria especializada em segurança da informação, governança, risco, compliance e cybersegurança. Plataforma SmartAudit para gestão integrada de riscos e maturidade." />
      </Helmet>

      <div className="min-h-screen" data-testid="home-page">
        {/* Hero Section */}
        <section
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          data-testid="hero-section"
        >
          {/* Cyber Background */}
          <CyberBackground />

          <div className="relative container mx-auto px-4 py-32 pt-40">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8 border border-white/10"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <Sparkles className="w-4 h-4 text-[#10b981]" />
                  <span className="text-sm text-gray-300">Plataforma SmartAudit + Consultoria Especializada</span>
                </motion.div>

                {/* Headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Eleve a <span className="text-[#10b981]">maturidade de segurança</span> da sua empresa com inteligência em riscos
                </h1>

                <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
                  Consultoria especializada em segurança da informação, governança, risco e compliance + 
                  plataforma proprietária <strong className="text-white">SmartAudit</strong> para gestão integrada
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contato"
                    data-testid="hero-cta-contact"
                    className="group bg-[#10b981] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#0ea573] transition-all duration-300 hover:shadow-2xl hover:shadow-[#10b981]/25 hover:scale-105 inline-flex items-center justify-center gap-2"
                  >
                    Falar com especialista
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/smartaudit"
                    data-testid="hero-cta-smartaudit"
                    className="group bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20 inline-flex items-center justify-center gap-2"
                  >
                    Conhecer o SmartAudit
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>

              {/* Floating Frameworks */}
              <motion.div 
                className="mt-16 flex flex-wrap justify-center gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {frameworks.map((framework, idx) => (
                  <span
                    key={framework}
                    className="px-4 py-2 bg-white/5 backdrop-blur-sm rounded-lg text-sm text-gray-400 border border-white/10 hover:border-[#10b981]/50 hover:text-white transition-all"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    {framework}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 10, 0] }}
            transition={{ delay: 1, duration: 1.5, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <div className="w-1 h-3 bg-white/50 rounded-full" />
            </div>
          </motion.div>
        </section>

        {/* About Section */}
        <section className="py-20 bg-white" data-testid="about-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                Sobre a DMM
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-6">
                Seu Advisor Estratégico em Segurança
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Somos um <strong className="text-[#1a3a52]">parceiro estratégico</strong> em segurança da informação, 
                governança, risco e compliance (GRC). Nossa missão é apoiar organizações a alcançar níveis elevados de 
                maturidade em segurança, através de uma abordagem consultiva, prática e sustentável. 
                Acreditamos em <strong className="text-[#1a3a52]">parcerias de longo prazo</strong>, construindo 
                relações de confiança e resultados concretos.
              </p>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 mt-8 text-[#10b981] font-semibold hover:gap-3 transition-all"
              >
                Conheça nossa história
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-gray-50" data-testid="services-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#1a3a52]/10 text-[#1a3a52] text-sm font-semibold rounded-full mb-4">
                Nossos Serviços
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                Soluções Completas em Segurança
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Oferecemos serviços integrados para elevar a maturidade e compliance da sua organização
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={service.link}
                    className="group block bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-[#10b981]/30 h-full"
                    data-testid={`service-card-${index}`}
                  >
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1a3a52] mb-3 group-hover:text-[#10b981] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-[#10b981] font-medium text-sm group-hover:gap-3 transition-all">
                      Saiba mais
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SmartAudit Highlight Section */}
        <section className="py-24 bg-gradient-to-br from-[#1a3a52] via-[#1e4d6b] to-[#1a3a52] text-white overflow-hidden relative" data-testid="smartaudit-section">
          {/* Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full" style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, rgba(16,185,129,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(59,130,246,0.3) 0%, transparent 50%)`
            }} />
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full mb-6">
                  <Zap className="w-4 h-4" />
                  Plataforma Proprietária
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  SmartAudit: Inteligência em Gestão de Riscos
                </h2>
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  Plataforma para <strong className="text-white">mapear processos</strong>, consolidar frameworks e 
                  <strong className="text-white"> calcular automaticamente</strong> scores de risco e maturidade, 
                  convergindo para o <strong className="text-[#10b981]">PPSI 2.0</strong> como hub central.
                </p>

                {/* Frameworks Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-8">
                  {['ISO 27001', 'NIST CSF 2.0', 'BIA', 'DPIA', 'TPRM', 'Riscos', 'SOX', 'Vulnerab.'].map((fw, idx) => (
                    <motion.div
                      key={fw}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.05 }}
                      className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-center text-sm font-medium border border-white/10 hover:border-[#10b981]/50 transition-colors"
                    >
                      {fw}
                    </motion.div>
                  ))}
                </div>

                <Link
                  to="/smartaudit"
                  className="group inline-flex items-center gap-2 bg-[#10b981] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0ea573] transition-all duration-300 hover:shadow-xl hover:shadow-[#10b981]/25"
                  data-testid="smartaudit-cta"
                >
                  Agendar demonstração
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>

              {/* Integration Flow Visual */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                  <h3 className="text-xl font-bold mb-6 text-center">Fluxo de Integração</h3>
                  
                  {/* Visual Flow */}
                  <div className="flex flex-col items-center gap-4">
                    {/* Source Modules */}
                    <div className="grid grid-cols-3 gap-3 w-full">
                      {['ISO 27001', 'NIST CSF', 'BIA'].map((mod) => (
                        <div key={mod} className="bg-blue-500/20 rounded-lg p-3 text-center text-sm border border-blue-500/30">
                          {mod}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3 w-full">
                      {['DPIA', 'TPRM', 'Riscos'].map((mod) => (
                        <div key={mod} className="bg-emerald-500/20 rounded-lg p-3 text-center text-sm border border-emerald-500/30">
                          {mod}
                        </div>
                      ))}
                    </div>
                    
                    {/* Arrow Down */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-0.5 h-8 bg-gradient-to-b from-white/20 to-[#10b981]" />
                      <span className="text-xs text-gray-400">Sincronização</span>
                    </div>

                    {/* PPSI Hub */}
                    <div className="bg-gradient-to-br from-[#10b981] to-emerald-600 rounded-xl p-6 text-center w-full max-w-xs shadow-xl shadow-[#10b981]/25">
                      <Lock className="w-8 h-8 mx-auto mb-2" />
                      <h4 className="font-bold text-lg">PPSI 2.0</h4>
                      <p className="text-sm text-emerald-100">Hub Central • 25 Controles</p>
                    </div>

                    {/* Arrow Down */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-0.5 h-8 bg-gradient-to-b from-[#10b981] to-white/20" />
                      <span className="text-xs text-gray-400">Consolidação</span>
                    </div>

                    {/* Results */}
                    <div className="grid grid-cols-3 gap-3 w-full">
                      {[
                        { label: 'Dashboard', color: 'bg-green-500/20 border-green-500/30' },
                        { label: 'Relatórios', color: 'bg-purple-500/20 border-purple-500/30' },
                        { label: 'KPIs', color: 'bg-orange-500/20 border-orange-500/30' }
                      ].map((result) => (
                        <div key={result.label} className={`${result.color} rounded-lg p-3 text-center text-sm border`}>
                          {result.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white" data-testid="benefits-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                Benefícios
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                Benefícios para sua Liderança
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#10b981]/10 flex items-center justify-center group-hover:bg-[#10b981] group-hover:scale-110 transition-all duration-300">
                    <benefit.icon className="w-8 h-8 text-[#10b981] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1a3a52] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Frameworks & Competencies Section */}
        <section className="py-20 bg-gray-50" data-testid="frameworks-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                Expertise
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                Frameworks & Competências
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Expertise comprovada nos principais frameworks e normas de segurança da informação
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 max-w-5xl mx-auto">
              {[
                { name: 'ISO 27001', color: 'from-blue-500 to-blue-600' },
                { name: 'NIST CSF 2.0', color: 'from-emerald-500 to-emerald-600' },
                { name: 'PPSI 2.0', color: 'from-cyan-500 to-cyan-600' },
                { name: 'LGPD', color: 'from-purple-500 to-purple-600' },
                { name: 'SOX', color: 'from-orange-500 to-orange-600' },
                { name: 'PCI-DSS', color: 'from-red-500 to-red-600' },
                { name: 'COBIT', color: 'from-indigo-500 to-indigo-600' },
              ].map((fw, index) => (
                <motion.div
                  key={fw.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className={`bg-gradient-to-br ${fw.color} rounded-xl p-4 text-center text-white font-bold shadow-lg cursor-default`}
                >
                  {fw.name}
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center text-gray-500 mt-8 text-sm"
            >
              + Expertise em BIA, DPIA, TPRM, Gestão de Vulnerabilidades e muito mais
            </motion.p>
          </div>
        </section>

        {/* Security Insights Section */}
        <SecurityInsights />

        {/* Testimonials Section */}
        <section className="py-20 bg-gray-50" data-testid="testimonials-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#1a3a52]/10 text-[#1a3a52] text-sm font-semibold rounded-full mb-4">
                Resultados
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                O Que Nossos Clientes Dizem
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto mb-5">
              {testimonials.slice(0, 4).map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 relative"
                >
                  <Quote className="absolute top-4 right-4 w-8 h-8 text-[#10b981]/10" />
                  <p className="text-gray-600 mb-4 italic leading-relaxed relative z-10 text-sm">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a3a52] to-[#10b981] flex items-center justify-center text-white font-bold text-xs">
                      {testimonial.initials}
                    </div>
                    <div>
                      <p className="font-bold text-[#1a3a52] text-sm">{testimonial.role}</p>
                      <p className="text-xs text-gray-500">{testimonial.sector}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {testimonials.slice(4, 7).map((testimonial, index) => (
                <motion.div
                  key={index + 4}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (index + 4) * 0.1 }}
                  className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 relative"
                >
                  <Quote className="absolute top-4 right-4 w-8 h-8 text-[#10b981]/10" />
                  <p className="text-gray-600 mb-4 italic leading-relaxed relative z-10 text-sm">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a3a52] to-[#10b981] flex items-center justify-center text-white font-bold text-xs">
                      {testimonial.initials}
                    </div>
                    <div>
                      <p className="font-bold text-[#1a3a52] text-sm">{testimonial.role}</p>
                      <p className="text-xs text-gray-500">{testimonial.sector}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#1a3a52] to-[#10b981] text-white relative overflow-hidden" data-testid="final-cta-section">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)`
            }} />
          </div>
          
          <div className="container mx-auto px-4 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Pronto para elevar a maturidade de segurança da sua empresa?
              </h2>
              <p className="text-xl mb-10 text-gray-200">
                Entre em contato com nossos especialistas e descubra como podemos transformar sua gestão de riscos e compliance
              </p>
              <Link
                to="/contato"
                className="group inline-flex items-center gap-2 bg-white text-[#1a3a52] px-10 py-5 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-2xl hover:scale-105"
                data-testid="final-cta-btn"
              >
                Falar com especialista
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
